// ── StatusRibbon ────────────────────────────────────────────────────────────
// The canonical diagonal corner ribbon used across posters app-wide.
// Green 'Done' for shows the viewer completed; orange 'Ended' / red 'Cancelled'
// for shows the network wrapped that the viewer is still tracking. Else null.
// Single source of truth so every poster surface stays visually consistent.
import { endedBadge } from '../lib/episode'
import { todayKey } from '../lib/datetime'
import { revivalInfo } from './RevivalBanner'

const RIBBON_WRAP = { position:'absolute', top:0, right:0, width:'100%', height:'100%', borderRadius:'var(--radius)', overflow:'hidden', pointerEvents:'none', zIndex:3 }
const RIBBON_BASE = {
  position:'absolute', top:18, right:-22, width:88,
  transform:'rotate(40deg)',
  color:'var(--text-strong)', fontSize:10, fontWeight:800,
  letterSpacing:'0.08em', textTransform:'uppercase',
  textAlign:'center', padding:'4px 0',
  boxShadow:'0 2px 8px rgba(0,0,0,0.5)',
  borderTop:'1px solid var(--border)',
  borderBottom:'1px solid var(--border)',
}

export function StatusRibbon({ show, status, wl }) {
  if (!show) return null
  // ABANDONED is PRIVATE by construction: every caller passes the VIEWER's own
  // wl.statuses, including FriendsWatching (which renders a friend's row but your status).
  // So this can never leak "X gave up on this" to anyone else.
  //
  // It also takes precedence over the Ended/Cancelled ribbon, which is the right call:
  // whether the network wrapped it stops mattering once you've walked away, and two
  // ribbons in one corner is worse than the wrong one. Statuses are exclusive, so this
  // can't collide with Done.
  if (status === 'dropped') {
    return (
      <div style={RIBBON_WRAP}>
        <div style={{ ...RIBBON_BASE,
          // Deliberately grey, not red. Abandoning isn't an error or a failure state —
          // it's a decision, and the palette shouldn't scold you for making it.
          background:'rgba(28,28,38,0.92)', color:'rgba(var(--text-rgb),0.62)' }}>
          Abandoned
        </div>
      </div>
    )
  }
  if (status === 'completed') {
    // A show marked Done can quietly get a new season (TMDB/RevivalBanner catches this
    // on the detail page already) — but Discover/MyList cards kept showing "Done" forever
    // since nothing here ever re-checked. Same detection, surfaced where the stale badge
    // actually lives (Jason 2026-08-02: "Lioness is showing ended, but there's a new season").
    const revival = revivalInfo(show, status, wl?.completedAt?.[show.id], wl?.progress?.[show.id])
    if (revival) {
      return (
        <div style={RIBBON_WRAP}>
          <div style={{ ...RIBBON_BASE, background:'linear-gradient(135deg,var(--purple),#8c7ae6)', color:'var(--amber-ink)' }}>📡 New Season</div>
        </div>
      )
    }
    return (
      <div style={RIBBON_WRAP}>
        <div style={{ ...RIBBON_BASE, background:'color-mix(in srgb, var(--state-good) 92%, transparent)' }}>✓ Done</div>
      </div>
    )
  }
  // Display badge only for a genuinely-AGED ended/cancelled show — endedBadge suppresses it
  // on recently-wrapped/limited series so a fresh drop doesn't look old & dead.
  const endedLbl = endedBadge(show, todayKey())
  const watchingTypes = ['watching', 'rewatching', 'plan_to_watch']
  if (endedLbl && (watchingTypes.includes(status) || !status)) {
    return (
      <div style={RIBBON_WRAP}>
        <div style={{ ...RIBBON_BASE, background: endedLbl === 'Cancelled' ? 'color-mix(in srgb, var(--state-bad) 88%, transparent)' : 'rgba(229,112,26,0.88)' }}>
          {endedLbl}
        </div>
      </div>
    )
  }
  return null
}
